; (() => {
  "use strict"

  const ORBE_MARKET_SELECTOR_MODAL_ID = "md-app-embed__footer-popup"
  const ORBE_WAIT_TIMEOUT_MS = 10000
  const ORBE_WAIT_INTERVAL_MS = 100

  /**
   * Orbe exposes `orbito` as a global lexical binding (let/const), not always on window.
   * DevTools can access `orbito` directly, but `window.orbito` may be undefined.
   * @returns {Record<string, unknown> | null}
   */
  function getOrbito() {
    if (globalThis.orbito) {
      return globalThis.orbito
    }

    try {
      // Access global lexical `orbito` from a strict module scope.
      const orbito = new Function(
        'return typeof orbito !== "undefined" ? orbito : null'
      )()

      return orbito || null
    } catch {
      return null
    }
  }

  /**
   * Waits until Orbe exposes the orbito API.
   * @returns {Promise<Record<string, unknown>>}
   */
  function waitForOrbito() {
    return new Promise((resolve, reject) => {
      const existingOrbito = getOrbito()

      if (existingOrbito) {
        resolve(existingOrbito)
        return
      }

      const startedAt = Date.now()

      const intervalId = window.setInterval(() => {
        const orbito = getOrbito()

        if (orbito) {
          window.clearInterval(intervalId)
          resolve(orbito)
          return
        }

        if (Date.now() - startedAt >= ORBE_WAIT_TIMEOUT_MS) {
          window.clearInterval(intervalId)
          reject(new Error("Orbe orbito not available"))
        }
      }, ORBE_WAIT_INTERVAL_MS)
    })
  }

  /**
   * Opens an Orbe modal using the JS API.
   * @param {Record<string, unknown>} orbito
   * @returns {boolean}
   */
  function openOrbeModalWithApi(orbito) {
    if (typeof orbito.openModalWithoutRefresh === "function") {
      orbito.openModalWithoutRefresh()
      return true
    }

    if (typeof orbito.openModal === "function") {
      orbito.openModal(ORBE_MARKET_SELECTOR_MODAL_ID)
      return true
    }

    if (typeof orbito.openAndRefreshModal === "function") {
      orbito.openAndRefreshModal()
      return true
    }

    return false
  }

  /**
   * Opens the Orbe country/language selector modal.
   * @returns {Promise<boolean>}
   */
  async function openOrbeLocalizationModal() {
    const orbito = getOrbito()

    if (orbito && openOrbeModalWithApi(orbito)) {
      return true
    }

    try {
      const readyOrbito = await waitForOrbito()
      return openOrbeModalWithApi(readyOrbito)
    } catch {
      console.warn("[localization-trigger] Orbe is not loaded")
      return false
    }
  }

  /**
   * Closes the mobile header menu before opening Orbe from the drawer.
   */
  function closeMobileMenuIfOpen() {
    const menu = document.querySelector("t-menu")
    const toggle = document.querySelector("[data-btn-toogle-menu].is-open")

    if (menu?.classList.contains("is-open") && toggle instanceof HTMLElement) {
      toggle.click()
    }
  }

  const LocalizationTrigger = class extends HTMLElement {
    connectedCallback() {
      this.addEventListener("click", this.onClick)
      this.addEventListener("keydown", this.onKeyDown)
    }

    onKeyDown = (event) => {
      if (event.key !== "Enter" && event.key !== " ") {
        return
      }

      event.preventDefault()
      closeMobileMenuIfOpen()
      void openOrbeLocalizationModal()
    }

    onClick = (event) => {
      event.preventDefault()
      closeMobileMenuIfOpen()
      void openOrbeLocalizationModal()
    }
  }

  if (!window.customElements.get("localization-trigger")) {
    window.customElements.define("localization-trigger", LocalizationTrigger)
  }
})()
